import { useState } from 'react';
import React from 'react';

export type TabProps = {
  id: string;
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
};

export type TabsProps = {
  children: React.ReactElement<TabProps>[];
  defaultActiveTab?: string;
  /** When set, Tabs is controlled; parent must update this on tab change (or via URL). */
  activeTab?: string;
  onActiveTabChange?: (tabId: string) => void;
};

export const TabPanel = ({ children, id, style }: TabProps) => (
  <div id={`tabpanel-${id}`} role="tabpanel" className="hub-tabs-content" style={style}>
    {children}
  </div>
);

const Tabs = ({ children, defaultActiveTab, activeTab: activeTabProp, onActiveTabChange }: TabsProps) => {
  const isControlled = activeTabProp !== undefined;
  const [internalActiveTab, setInternalActiveTab] = useState<string>(
    defaultActiveTab ?? (children[0]?.props.id ?? ''),
  );

  const resolvedActiveTab = isControlled ? activeTabProp : internalActiveTab;

  const tabs = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<TabProps> =>
      React.isValidElement<TabProps>(child) && typeof child.props.id === 'string',
  );

  const firstId = tabs[0]?.props.id ?? '';
  const fallbackActiveTab = tabs.some((t) => t.props.id === resolvedActiveTab)
    ? resolvedActiveTab
    : defaultActiveTab && tabs.some((t) => t.props.id === defaultActiveTab)
      ? defaultActiveTab
      : firstId;

  const setTab = (id: string): void => {
    if (!isControlled) {
      setInternalActiveTab(id);
    }
    onActiveTabChange?.(id);
  };

  return (
    <div className="hub-tabs-container">
      <div className="hub-tabs-header" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.props.id}
            id={`tab-${tab.props.id}`}
            role="tab"
            type="button"
            aria-selected={fallbackActiveTab === tab.props.id}
            aria-controls={`tabpanel-${tab.props.id}`}
            className={`h4 hub-tab-button${fallbackActiveTab === tab.props.id ? ' is-active' : ''}`}
            onClick={() => setTab(tab.props.id)}
          >
            {tab.props.label}
          </button>
        ))}
      </div>
      {tabs.map((tab) =>
        React.cloneElement(tab, {
          key: tab.props.id,
          style: { display: fallbackActiveTab === tab.props.id ? 'block' : 'none' },
        }),
      )}
    </div>
  );
};

export default Tabs;
